<template>
  <Teleport to="body">
    <div v-if="open" class="campaign-modal" role="dialog" aria-modal="true" @click.self="close">
      <div class="campaign-modal__panel">
        <button type="button" class="campaign-modal__close" :aria-label="t('campaign.close')" @click="close">
          <span class="material-symbols-outlined">close</span>
        </button>

        <div v-if="loading" class="campaign-modal__state">
          <span class="campaign-modal__spinner"></span>
        </div>
        <p v-else-if="loadError" class="campaign-modal__state campaign-modal__state--error">{{ loadError }}</p>
        <template v-else-if="campaign">
          <CampaignCard
            :image-url="campaignImageUrl(campaign.imagePath)"
            :title="campaign.title"
            :body-html="resolveCampaignHtml(campaign.bodyHtml)"
            :button-text="campaign.buttonKind !== 'none' ? campaign.buttonText : ''"
            :button-busy="busy"
            :done-text="leadDone ? leadDoneText : ''"
            @action="handleAction"
          />
          <p v-if="actionError" class="campaign-modal__error">{{ actionError }}</p>
        </template>
      </div>
    </div>
  </Teleport>
</template>

<script setup>
import { ref, computed, watch, onUnmounted } from 'vue'
import { useRouter } from 'vue-router'
import { t } from '../services/i18nService'
import CampaignCard from './CampaignCard.vue'
import {
  campaignImageUrl,
  resolveCampaignHtml,
  fetchCampaign,
  markCampaignSeen,
  markCampaignClick,
  sendCampaignInterest,
} from '../services/campaignService'

// Окно кампании дилера: открывается при входе (popup), из колокольчика и по ссылке из push.
// Кампанию либо передают готовой (popup), либо грузим по id. Кнопки «не показувати» нет:
// от внутренних сообщений дилера клиент не отписывается (решение 19.10.2026)
const props = defineProps({
  open: { type: Boolean, default: false },
  campaignId: { type: Number, default: null },
  preloaded: { type: Object, default: null },
  fromPopup: { type: Boolean, default: false },
})
const emit = defineEmits(['close'])

const router = useRouter()
const campaign = ref(null)
const loading = ref(false)
const loadError = ref('')
const actionError = ref('')
const busy = ref(false)
const leadDone = ref(false)

const leadDoneText = computed(() =>
  campaign.value?.dealerName
    ? t('campaign.leadDone', { dealer: campaign.value.dealerName })
    : t('campaign.leadDoneNoDealer')
)

const reset = () => {
  campaign.value = null
  loadError.value = ''
  actionError.value = ''
  busy.value = false
  leadDone.value = false
}

const load = async () => {
  reset()
  if (props.preloaded) {
    campaign.value = props.preloaded
  } else if (props.campaignId) {
    loading.value = true
    try {
      const res = await fetchCampaign(props.campaignId)
      campaign.value = res?.campaign || null
      if (!campaign.value) loadError.value = t('campaign.loadError')
    } catch {
      loadError.value = t('campaign.loadError')
    } finally {
      loading.value = false
    }
  }
  if (campaign.value) {
    leadDone.value = !!campaign.value.leadRequested
    // Отметка «увидел»: статистика дилера и прочитано в колокольчике; ошибка не мешает показу
    markCampaignSeen(campaign.value.id, props.fromPopup).catch(() => {})
  }
}

watch(() => [props.open, props.campaignId, props.preloaded], () => {
  if (props.open) load()
}, { immediate: true })

const close = () => {
  emit('close')
}

const handleAction = async () => {
  const c = campaign.value
  if (!c || busy.value) return
  actionError.value = ''
  if (c.buttonKind === 'link' && c.buttonUrl) {
    markCampaignClick(c.id).catch(() => {})
    // Путь портала открываем внутри, внешнюю ссылку в новой вкладке
    if (c.buttonUrl.startsWith('/')) {
      close()
      router.push(c.buttonUrl)
    } else {
      window.open(c.buttonUrl, '_blank', 'noopener')
    }
    return
  }
  if (c.buttonKind === 'lead') {
    busy.value = true
    try {
      await sendCampaignInterest(c.id)
      leadDone.value = true
    } catch {
      actionError.value = t('campaign.actionError')
    } finally {
      busy.value = false
    }
  }
}

const onKeydown = (e) => {
  if (e.key === 'Escape' && props.open) close()
}
watch(() => props.open, (isOpen) => {
  if (isOpen) document.addEventListener('keydown', onKeydown)
  else document.removeEventListener('keydown', onKeydown)
}, { immediate: true })

onUnmounted(() => {
  document.removeEventListener('keydown', onKeydown)
})
</script>

<style scoped>
.campaign-modal {
  position: fixed;
  inset: 0;
  z-index: 10100;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: var(--media-overlay);
}

.campaign-modal__panel {
  position: relative;
  width: 100%;
  max-width: 520px;
  max-height: calc(100vh - 32px);
  overflow-y: auto;
  border-radius: var(--radius-lg);
  background: var(--bg-card);
  box-shadow: var(--shadow-lg);
}

.campaign-modal__close {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border: none;
  border-radius: 50%;
  background: var(--bg-card);
  color: var(--text-primary);
  box-shadow: var(--shadow-md);
  cursor: pointer;
}

.campaign-modal__state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  margin: 0;
  padding: 24px;
  color: var(--text-secondary);
  text-align: center;
}

.campaign-modal__state--error {
  color: var(--danger-text);
}

.campaign-modal__spinner {
  width: 28px;
  height: 28px;
  border: 3px solid var(--border-default);
  border-top-color: var(--primary);
  border-radius: 50%;
  animation: campaign-spin 0.8s linear infinite;
}

@keyframes campaign-spin {
  to { transform: rotate(360deg); }
}

.campaign-modal__error {
  margin: 0 22px 12px;
  color: var(--danger-text);
  font-size: 0.875rem;
}

/* Телефон: окно на весь экран, крестик под вырезом iPhone */
@media (max-width: 480px) {
  .campaign-modal {
    padding: 0;
    align-items: stretch;
  }

  .campaign-modal__panel {
    max-width: none;
    max-height: none;
    height: 100%;
    border-radius: 0;
    padding-bottom: env(safe-area-inset-bottom, 0px);
  }

  .campaign-modal__close {
    top: calc(10px + env(safe-area-inset-top, 0px));
  }
}
</style>
